/**
 * Profile Settings Component
 *
 * Allows users to customize their display name and avatar.
 * The display name is used in presence indicators across the admin panel.
 *
 * Features:
 * - Set custom display name (falls back to firstName lastName if not set)
 * - Upload avatar image (stored in S3/CloudFront)
 * - Preview of how name appears in presence indicators
 *
 * Part of Feature 2: Real-time Collaboration
 */

import { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Avatar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNotify } from 'react-admin';
import config from '../config';
import { getStoredToken } from '../services/tokenRefreshService';
import Logger from '../utils/Logger';

interface ProfileData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string | null;
  avatarUrl: string | null;
  name: string;
  roles: string[];
  mfaEnabled: boolean;
}

interface ProfileSettingsProps {
  onProfileUpdate?: () => void;
}

export const ProfileSettings = ({ onProfileUpdate }: ProfileSettingsProps) => {
  const notify = useNotify();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Fetch current profile
  useEffect(() => {
    const fetchProfile = async () => {
      const token = getStoredToken();
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${config.API_URL}/admin/users/profile/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch profile');
        }

        const data = await response.json();
        setProfile(data.profile);
        setDisplayName(data.profile.displayName || '');
        setAvatarUrl(data.profile.avatarUrl || '');
      } catch (err) {
        Logger.error('[Profile Settings] Fetch error:', err);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleSave = async () => {
    const token = getStoredToken();
    if (!token) {
      notify('Not authenticated', { type: 'error' });
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(`${config.API_URL}/admin/users/profile/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          displayName: displayName.trim() || null,
          avatarUrl: avatarUrl.trim() || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update profile');
      }

      const data = await response.json();
      setProfile(data.profile);
      notify('Profile updated successfully', { type: 'success' });

      // Notify parent to refresh
      if (onProfileUpdate) {
        onProfileUpdate();
      }
    } catch (err) {
      Logger.error('[Profile Settings] Save error:', err);
      notify(err instanceof Error ? err.message : 'Failed to update profile', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Handle avatar file upload
  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      notify('Invalid file type. Please upload JPEG, PNG, WebP, or GIF.', { type: 'error' });
      return;
    }

    // Validate file size (2MB max)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      notify('File too large. Maximum size is 2MB.', { type: 'error' });
      return;
    }

    const token = getStoredToken();
    if (!token) {
      notify('Not authenticated', { type: 'error' });
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await fetch(`${config.API_URL}/admin/users/profile/me/avatar`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload avatar');
      }

      const data = await response.json();
      setAvatarUrl(data.avatarUrl);
      notify('Avatar uploaded successfully', { type: 'success' });

      // Notify parent to refresh immediately (since avatar is saved to DB on upload)
      if (onProfileUpdate) {
        onProfileUpdate();
      }
    } catch (err) {
      Logger.error('[Profile Settings] Upload error:', err);
      notify(err instanceof Error ? err.message : 'Failed to upload avatar', { type: 'error' });
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle avatar removal
  const handleRemoveAvatar = () => {
    setAvatarUrl('');
  };

  // Get the preview name (what will show in presence)
  const getPreviewName = () => {
    if (displayName.trim()) {
      return displayName.trim();
    }
    if (profile) {
      const fullName = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
      return fullName || profile.email;
    }
    return 'Unknown';
  };

  // Get avatar initial for preview
  const getAvatarInitial = () => {
    const name = getPreviewName();
    return name.charAt(0).toUpperCase();
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Profile Settings</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" paragraph>
          Customize how your name and avatar appear to other admins in the panel.
          Your display name is shown in presence indicators when collaborating.
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Preview Section */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Preview (how others see you)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <Avatar
              src={avatarUrl || undefined}
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'primary.main',
                fontSize: '1rem',
              }}
            >
              {!avatarUrl && getAvatarInitial()}
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight={500}>
                {getPreviewName()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {profile?.email}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Form Fields */}
        <Stack spacing={3}>
          <TextField
            label="Display Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={profile ? `${profile.firstName} ${profile.lastName}`.trim() : ''}
            helperText={`Leave empty to use your full name: ${profile ? `${profile.firstName} ${profile.lastName}`.trim() : ''}`}
            fullWidth
            inputProps={{ maxLength: 100 }}
          />

          {/* Avatar Upload Section */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Profile Picture
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'action.hover',
              }}
            >
              <Avatar
                src={avatarUrl || undefined}
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: 'primary.main',
                  fontSize: '1.5rem',
                }}
              >
                {!avatarUrl && getAvatarInitial()}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleAvatarUpload}
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  style={{ display: 'none' }}
                  id="avatar-upload"
                />
                <Stack direction="row" spacing={1}>
                  <label htmlFor="avatar-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      size="small"
                      startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                      disabled={uploading}
                    >
                      {uploading ? 'Uploading...' : 'Upload Image'}
                    </Button>
                  </label>
                  {avatarUrl && (
                    <Tooltip title="Remove avatar">
                      <IconButton
                        size="small"
                        onClick={handleRemoveAvatar}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  JPEG, PNG, WebP, or GIF. Max 2MB. Cropped square, up to 512×512.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Stack>

        {/* Info about current settings */}
        {profile && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Current account:</strong> {profile.email}
              <br />
              <strong>Role:</strong> {profile.roles?.join(', ') || 'N/A'}
            </Typography>
          </Alert>
        )}

        {/* Save Button */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProfileSettings;
